import { useMutation, useQueryClient } from "react-query";
import { TSetor } from "../../../../../../types/api/estoque/setores";
import { useMemo } from "react";
import axiosClient from "../../../../../../lib/axios";
import { enqueueSnackbar } from "notistack";
import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel, Grid, Radio, RadioGroup, TextField, Typography } from "@mui/material";
import { TOpenModalSetores } from "..";
import { formatarInputDecimal, permitirApenasNumeros } from "../../../../../../helpers/masks";
import { Controller, FormProvider, useForm } from "react-hook-form";

export default function DialogCriacaoEditSetores({
  open,
  handleClose,
  defaultValue,
}: DialogSetoresProps) {
  const queryClient = useQueryClient();

  const isEdit = useMemo(() => Boolean(defaultValue), [defaultValue])
  const valoresIniciais = useMemo(() => defaultValue?.defaultValue ? defaultValue.defaultValue : null, [defaultValue])

  const methods = useForm<TSetor>({
    defaultValues: {
      id: valoresIniciais?.id,
      nome: valoresIniciais?.nome || "",
      margem_lucro: valoresIniciais?.margem_lucro.replace('.', ',') || "",
      desconto: valoresIniciais?.desconto.replace('.', ',') || "",
      acrescimo: valoresIniciais?.acrescimo.replace('.', ',') || "",
      maximo_parcelas: valoresIniciais?.maximo_parcelas || "",
      usa_condicoes_pag: valoresIniciais?.usa_condicoes_pag || "N",
      permitir_desconto: valoresIniciais?.permitir_desconto || "N",
      emite_receita: valoresIniciais?.emite_receita || "N",
    }
  });

  const { handleSubmit, control, getValues } = methods;

  //Querys e mutations
  const { mutate: criarSetor, isLoading: isLoadingRegister } = useMutation({
    mutationKey: "criar-setor",
    mutationFn: (inputs: TSetor) =>
      axiosClient.post("estoque/configuracoes/criar-setor", inputs),
    onError: (error) => {
      console.error(error);
      enqueueSnackbar("Erro ao cadastrar setor!", { variant: "error" });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["pesquisar-setores"]);
      handleClose();
      enqueueSnackbar("Setor cadastrado com sucesso!", {
        variant: "success",
      });
    },
  });

  const { mutate: editarSetor, isLoading: isLoadingUpdate } = useMutation({
    mutationKey: "editar-setor",
    mutationFn: (inputs: TSetor) =>
      axiosClient.put(
        `estoque/configuracoes/atualizar-setor/${valoresIniciais?.id}`,
        inputs
      ),
    onError: (error) => {
      console.error(error);
      enqueueSnackbar("Erro ao atualizar setor!", { variant: "error" });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["pesquisar-setores"]);
      handleClose();
      enqueueSnackbar("Setor atualizado com sucesso!", {
        variant: "success",
      });
    },
  });
  //

  const onSubmit = (data: TSetor) => {
    const paylod = {
      id: data.id,
      nome: data.nome,
      margem_lucro: data.margem_lucro.replace(',', '.') || "0",
      desconto: data.desconto.replace(',', '.') || "0",
      acrescimo: data.acrescimo.replace(',', '.') || "0",
      maximo_parcelas: data.maximo_parcelas,
      usa_condicoes_pag: data.usa_condicoes_pag,
      permitir_desconto: data.permitir_desconto,
      emite_receita: data.emite_receita,
    };

    if (isEdit) {
      editarSetor(paylod);
    } else {
      criarSetor(paylod);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {isEdit ? "Editar" : "Cadastrar"} Setor
        </DialogTitle>

        <DialogContent>
          <FormProvider {...methods}>
            <Grid container spacing={2} mt={1}>
              <Grid size={{xs: 12, md: 4}}>
                <Controller
                  name="nome"
                  control={control}
                  rules={{ required: 'Campo obrigatório' }}
                  render={({ field, fieldState: { error, invalid } }) => (
                    <TextField
                      {...field}
                      fullWidth
                      required
                      label="Nome"
                      value={field.value}
                      onChange={(e) =>
                        field.onChange(e.target.value)}
                      error={invalid}
                      helperText={error?.message || ""}
                    />
                  )}
                />
              </Grid>

              <Grid size={{xs: 12, md: 4}}>
                <Controller
                  name="id"
                  control={control}
                  rules={{ required: 'Campo obrigatório' }}
                  render={({ field, fieldState: { error, invalid } }) => (
                    <TextField
                      {...field}
                      fullWidth
                      required
                      label="Código"
                      value={field.value}
                      onChange={(e) =>
                        field.onChange(parseInt(permitirApenasNumeros(e.target.value)))}
                      error={invalid}
                      helperText={error?.message || ""}
                      disabled={isEdit ? true : false}
                    />
                  )}
                />
              </Grid>

              <Grid size={{xs: 12, md: 4}}>
                <Controller
                  name="maximo_parcelas"
                  control={control}
                  rules={{ required: 'Campo obrigatório' }}
                  render={({ field, fieldState: { error, invalid } }) => (
                    <TextField
                      {...field}
                      fullWidth
                      required
                      label="Máximo de Parcelas"
                      value={field.value}
                      onChange={(e) =>
                        field.onChange(permitirApenasNumeros(e.target.value))}
                      error={invalid}
                      helperText={error?.message || ""}
                    />
                  )}
                />
              </Grid>

              <Grid size={{xs: 12, md: 4}}>
                <Controller
                  name="margem_lucro"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Margem de Lucro (%)"
                      value={field.value}
                      onChange={(e) => {
                        const rawValue = permitirApenasNumeros(e.target.value);
                        const formattedValue = formatarInputDecimal(rawValue);
                        field.onChange(formattedValue);
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid size={{xs: 12, md: 4}}>
                <Controller
                  name="desconto"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Desconto (%)"
                      value={field.value}
                      onChange={(e) => {
                        const rawValue = permitirApenasNumeros(e.target.value);
                        const formattedValue = formatarInputDecimal(rawValue);
                        field.onChange(formattedValue);
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid size={{xs: 12, md: 4}}>
                <Controller
                  name="acrescimo"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Acréscimo (%)"
                      value={field.value}
                      onChange={(e) => {
                        const rawValue = permitirApenasNumeros(e.target.value);
                        const formattedValue = formatarInputDecimal(rawValue);
                        field.onChange(formattedValue);
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid size={{xs: 12, md: 4}}>
                <Typography gutterBottom>
                  Usar Condições de Pagamento?
                </Typography>
                <Controller
                  name="usa_condicoes_pag"
                  control={control}
                  render={({ field }) => (
                    <FormControl {...field}>
                      <RadioGroup
                        row
                        aria-labelledby="usa_condicoes_pag"
                        defaultValue={getValues("usa_condicoes_pag")}
                        onChange={(e) => field.onChange(e.target.checked)}
                      >
                        <FormControlLabel value="S" control={<Radio />} label="Sim" />
                        <FormControlLabel value="N" control={<Radio />} label="Não" />
                      </RadioGroup>
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid size={{xs: 12, md: 4}}>
                <Typography gutterBottom>Permitir Desconto?</Typography>
                <Controller
                  name="permitir_desconto"
                  control={control}
                  render={({ field }) => (
                    <FormControl {...field}>
                      <RadioGroup
                        row
                        aria-labelledby="permitir_desconto"
                        defaultValue={getValues("permitir_desconto")}
                        onChange={(e) => field.onChange(e.target.checked)}
                      >
                        <FormControlLabel value="S" control={<Radio />} label="Sim" />
                        <FormControlLabel value="N" control={<Radio />} label="Não" />
                      </RadioGroup>
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid size={{xs: 12, md: 4}}>
                <Typography gutterBottom>Emite Receita?</Typography>
                <Controller
                  name="emite_receita"
                  control={control}
                  render={({ field }) => (
                    <FormControl {...field}>
                      <RadioGroup
                        row
                        aria-labelledby="emite_receita"
                        defaultValue={getValues("emite_receita")}
                        onChange={(e) => field.onChange(e.target.checked)}
                      >
                        <FormControlLabel value="S" control={<Radio />} label="Sim" />
                        <FormControlLabel value="N" control={<Radio />} label="Não" />
                      </RadioGroup>
                    </FormControl>
                  )}
                />
              </Grid>
            </Grid>
          </FormProvider>
        </DialogContent>

        <DialogActions>
          <Button variant="outlined" onClick={handleClose}>
            Cancelar
          </Button>

          <Button
            variant="contained"
            onClick={handleSubmit(onSubmit)}
            disabled={isLoadingRegister || isLoadingUpdate}
          >
            {(isLoadingRegister || isLoadingUpdate) && (
              <CircularProgress size={20} sx={{ marginRight: 1 }} />
            )}
            {isEdit ? "Atualizar" : "Cadastrar"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

interface DialogSetoresProps {
  open: boolean;
  handleClose: () => void;
  defaultValue: TOpenModalSetores;
}
